<template>
  <div class="h-full flex flex-col items-center py-8 px-4">
    <div class="mobile-padding"></div>
    <div class="liquidGlassCard rounded-3xl w-full h-full pb-6 pt-14 flex flex-col">
      <button class="absolute w-8 h-8 left-5 top-5 " @click="$router.back">
        <ColorImg
          src="/images/icons/back.png"
          class="bg-current w-full h-full"
          alt=""
        />
      </button>
      <div class="absolute left-16 text-2xl top-5 title-color ">Boutique Rapidoo</div>
      
      <div class="px-4 overflow-y-auto flex-1 h-full">
        <div class="mobile-padding"></div>
        <DataFetcher
          dataKey="riderProducts"
          ref="productsGetter"
        >
          <template #any="{ loading, loadingError, refresh, data }">
            <div v-if="loading" class="flex flex-col gap-4">
              <div v-for="i in 3" :key="i" class="shimmerContainer h-40 rounded-2xl w-full"></div>
            </div>
            
            <div v-else-if="loadingError" class="flex flex-col items-center justify-center p-8 text-center">
              <NoDataView :refresh="refresh" />
            </div>
            
            <div v-else-if="!data || data.length === 0" class="flex flex-col items-center justify-center p-8 text-center">
              <div class="w-32 h-32 mb-4 opacity-20">
                <ColorImg src="/images/icons/shopping-bag.svg" class="w-full h-full bg-current" />
              </div>
              <div class="text-xl font-bold title-color">Aucun produit disponible</div>
              <p class="text-gray-500">Revenez plus tard pour voir les nouveaux équipements.</p>
            </div>
            
            <div v-else class="grid grid-cols-1 gap-4">
              <div 
                v-for="product in data" 
                :key="product.id"
                class="bg-white/50 backdrop-blur-sm rounded-2xl p-4 border flex gap-4 transition-all hover:border-primary-300"
              >
                <div class="w-24 h-24 min-w-24 rounded-xl overflow-hidden bg-gray-100">
                  <ImgComponent 
                    :src="product.image || '/images/sample/product-placeholder.png'" 
                    class="w-full h-full object-cover"
                  />
                </div>
                
                <div class="flex-1 flex flex-col justify-between">
                  <div>
                    <div class="font-bold title-color text-lg">{{ product.name }}</div>
                    <div class="text-xs text-gray-500 line-clamp-2 mt-1">{{ product.description }}</div>
                  </div>
                  
                  <div class="flex justify-between items-end mt-2">
                    <div class="text-primary font-bold text-xl">{{ formatPrice(product.price) }}</div>
                    <button 
                      @click="openPurchaseModal(product)"
                      class="bg-primary text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-sm hover:bg-primary-600 transition"
                    >
                      Acheter
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </DataFetcher>
      </div>
    </div>

    <!-- Purchase Confirmation BottomSheet -->
    <BottomSheet ref="purchaseBottomSheet" :overlayerCanDismiss="!isSubmitting">
      <div v-if="selectedProduct" class="p-6 relative">
        <h3 class="text-2xl font-bold title-color mb-5 pr-8">Finaliser la commande</h3>
        
        <div class="flex gap-4 mb-6 bg-white/50 p-3 rounded-2xl border">
          <div class="w-16 h-16 min-w-16 rounded-xl overflow-hidden bg-gray-100">
            <ImgComponent :src="selectedProduct.image || '/images/sample/product-placeholder.png'" class="w-full h-full object-cover" />
          </div>
          <div class="flex-1">
            <div class="font-bold text-lg leading-tight">{{ selectedProduct.name }}</div>
            <div class="text-primary font-black mt-1">{{ formatPrice(selectedProduct.price) }} / unité</div>
          </div>
        </div>
        
        <div class="space-y-4 mb-6">
          <div class="grid grid-cols-2 gap-4">
            <div class="bg-gray-50 rounded-xl p-3 border">
              <label class="text-[10px] uppercase font-bold text-gray-500 block mb-2">Quantité</label>
              <div class="flex items-center justify-between">
                <button @click="quantity > 1 ? quantity-- : null" class="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center text-lg font-bold text-primary">-</button>
                <span class="text-xl font-black text-gray-800">{{ quantity }}</span>
                <button @click="quantity++" class="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center text-lg font-bold text-primary">+</button>
              </div>
            </div>
            
            <div class="bg-gray-50 rounded-xl p-3 border flex flex-col justify-center">
              <label class="text-[10px] uppercase font-bold text-gray-500 block mb-1">Total</label>
              <div class="text-xl font-black text-primary">{{ formatPrice(selectedProduct.price * quantity) }}</div>
            </div>
          </div>
          
          <div>
            <label class="text-xs font-bold text-gray-600 block mb-1">Téléphone de livraison</label>
            <input 
              v-model="deliveryPhone" 
              type="tel" 
              placeholder="Ex: 01 22 33 44 55" 
              class="w-full p-3 rounded-xl border border-gray-200 bg-white/80 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all font-bold"
            />
          </div>

          <div>
            <label class="text-xs font-bold text-gray-600 block mb-1">Adresse de livraison (Optionnel)</label>
            <input 
              v-model="deliveryAddress" 
              type="text" 
              placeholder="Lieu de livraison souhaité" 
              class="w-full p-3 rounded-xl border border-gray-200 bg-white/80 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
            />
          </div>

          <!-- <div>
            <label class="text-xs font-bold text-gray-600 block mb-2">Mode de paiement</label>
            <div class="grid grid-cols-3 gap-2">
              <button 
                @click="paymentMethod = 'later'"
                class="flex flex-col items-center justify-center p-2 rounded-xl border-2 transition-all"
                :class="paymentMethod === 'later' ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200 text-gray-500'"
              >
                <svg class="w-5 h-5 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                <span class="text-[10px] font-bold text-center leading-tight">Payer<br/>plus tard</span>
              </button>
              
              <button 
                @click="paymentMethod = 'wallet'"
                class="flex flex-col items-center justify-center p-2 rounded-xl border-2 transition-all"
                :class="paymentMethod === 'wallet' ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200 text-gray-500'"
              >
                <svg class="w-5 h-5 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path></svg>
                <span class="text-[10px] font-bold text-center leading-tight">Solde<br/>Wallet</span>
              </button>

              <button 
                @click="paymentMethod = 'mobile_money'"
                class="flex flex-col items-center justify-center p-2 rounded-xl border-2 transition-all"
                :class="paymentMethod === 'mobile_money' ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200 text-gray-500'"
              >
                <svg class="w-5 h-5 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path></svg>
                <span class="text-[10px] font-bold text-center leading-tight">Mobile<br/>Money</span>
              </button>
            </div>
          </div> -->

          <!-- Extra inputs for mobile money if selected -->
          <!-- <div v-if="paymentMethod === 'mobile_money'" class="bg-gray-50 p-3 rounded-xl border border-gray-100 flex gap-2">
            <select v-model="provider" class="w-24 p-2 rounded-lg border border-gray-200 font-bold text-sm outline-none">
              <option value="mtn">MTN</option>
              <option value="moov">Moov</option>
            </select>
            <input 
              v-model="paymentPhone" 
              type="tel" 
              placeholder="Numéro payeur" 
              class="flex-1 p-2 rounded-lg border border-gray-200 outline-none font-bold text-sm tracking-wide"
            />
          </div> -->
        </div>
        
        <button 
          @click="confirmPurchase" 
          class="w-full py-4 rounded-2xl font-black text-white shadow-lg transition-all"
          :class="isSubmitting ? 'bg-gray-400' : 'bg-primary shadow-primary/30 hover:shadow-xl hover:shadow-primary/40'"
          :disabled="isSubmitting"
        >
          <span v-if="isSubmitting" class="flex items-center justify-center gap-2">
            <svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
            Traitement...
          </span>
          <span v-else>
            {{ paymentMethod === 'later' ? 'Commander (Payer plus tard)' : `Payer ${formatPrice(selectedProduct.price * quantity)}` }}
          </span>
        </button>
        <div class="h-8"></div>
      </div>
    </BottomSheet>

    <ActionTrigger
      ref="purchaseTrigger"
      dataKey="buyProduct"
      @success="onPurchaseSuccess"
      @failed="onPurchaseError"
    />
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import ColorImg from '@/presentation/components/img/ColorImg.vue'
import ImgComponent from '@/presentation/components/img/ImgComponent.vue'
import DataFetcher from '@/features/data/presentation/components/DataFetcher.vue'
import NoDataView from '@/features/data/presentation/components/NoDataView.vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import BottomSheet from '@/presentation/components/dialog/BottomSheet.vue'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'
import Storage from '@/shared/utils/storage'

const router = useRouter()

const purchaseBottomSheet = ref(null)
const selectedProduct = ref(null)
const quantity = ref(1)
const deliveryAddress = ref('')
const deliveryPhone = ref('')
const paymentPhone = ref('')
const paymentMethod = ref('later') // 'later', 'wallet', 'mobile_money'
const provider = ref('mtn')
const isSubmitting = ref(false)
const purchaseTrigger = ref()

const riderData = Storage.get('userData')

function formatPrice(price) {
  return new Intl.NumberFormat('fr-BJ', {
    style: 'currency',
    currency: 'XOF',
    maximumFractionDigits: 0,
  }).format(price || 0)
}

function openPurchaseModal(product) {
  selectedProduct.value = product
  quantity.value = 1
  deliveryPhone.value = riderData?.phoneNumber || ''
  paymentPhone.value = riderData?.phoneNumber || ''
  deliveryAddress.value = ''
  paymentMethod.value = 'later'
  isSubmitting.value = false
  purchaseBottomSheet.value?.show()
}

function confirmPurchase() {
  if (!deliveryPhone.value) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Attention',
      body: 'Veuillez renseigner un numéro de téléphone pour la livraison.',
      type: 'warning'
    })
    return
  }
  
  if (paymentMethod.value === 'mobile_money' && (!paymentPhone.value || paymentPhone.value.length < 8)) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Attention',
      body: 'Veuillez vérifier le numéro payeur Mobile Money.',
      type: 'warning'
    })
    return
  }

  if (!selectedProduct.value?.id) return
  
  isSubmitting.value = true
  
  const purchaseData = {
    productId: selectedProduct.value.id,
    quantity: quantity.value,
    deliveryAddress: deliveryAddress.value || 'N/A',
    phone: deliveryPhone.value,
    riderId: riderData?.id || riderData?._id,
    riderName: riderData?.name || `${riderData?.firstName || ''} ${riderData?.lastName || ''}`.trim()
  }

  // Only add payment fields if not 'later'
  if (paymentMethod.value !== 'later') {
    purchaseData.paymentMethod = paymentMethod.value
    if (paymentMethod.value === 'mobile_money') {
      purchaseData.provider = provider.value
      purchaseData.phoneNumber = paymentPhone.value.replace(/\s/g, '')
    }
  }

  purchaseTrigger.value?.trigger(purchaseData)
}

function onPurchaseSuccess(res) {
  isSubmitting.value = false
  purchaseBottomSheet.value?.hide()
  
  const isPaid = res?.result?.data?.status === 'paye'
  
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Commande validée !',
    body: isPaid ? 'Achat réglé avec succès.' : 'Votre commande a été enregistrée. Pensez à la régler plus tard.',
    type: 'success'
  })
  
  // Navigate to my purchases page using Vue Router
  setTimeout(() => {
    router.push({ name: 'myPurchases' })
  }, 600)
}

function onPurchaseError(err) {
  isSubmitting.value = false
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Échec de l\'achat',
    body: err?.result?.message || err?.error?.message || 'Une erreur est survenue lors de la commande.',
    type: 'error'
  })
}
</script>

<script>
export default {
  name: 'RiderShop'
}
</script>
